import React, { useEffect, useCallback, useState, useRef } from 'react'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import propTypes from 'prop-types'
import { renderable } from 'src/common/propTypes'
import Footer from './Footer'
import styles from './index.module.scss'
import { useAnalytics } from '../../common/analytics'
import { useFeatureFlags } from 'src/common/ab-testing/context'
import { featureToggles } from 'src/common/constants/featureToggles'
import { experimentSectionByVariation } from 'src/common/analytics/consts'
import { useLeadId } from 'src/common/lead-id'
import axios from 'axios'
import { useServerSideExperiments } from 'src/common/hooks/useServerSideExperiments'
import { defaultNumber } from './consts'
import { trackVisitor } from 'src/common/analytics/utils'
import { useProspectQuizNudge } from 'src/hooks/useProspectQuizNudge'
import { getCookie } from 'src/common/helpers'
import { useInactiveLeadPznPopup } from 'src/common/hooks/useInactiveLeadPznPopup'
import OutageBanner from '../pages/Home/OutageBanner'
import classNames from 'classnames'
import Megamenu from './Megamenu'
import ClientDashboardLoginOverlayGate from './ClientDashboardLoginOverlayGate'
import { ClientDashboardLoginProvider } from './ClientDashboardLoginContext'

import {
  LazyInactiveLeadPopup,
  LazyProspectQuiz,
  LazyProspectQuizNudge,
} from './lazyPopups'
import ActualHeader from './ActualHeader'
import { isTalkToUsPath } from 'src/common/utils/isTalkToUsPath'

// browser_id polling for the chatbot flag: 20 × 500ms = up to 10s.
const BROWSER_ID_RETRY_MS = 500
const BROWSER_ID_MAX_RETRIES = 20

const Layout = ({
  children,
  footerClassName,
  withHeaderTel = false,
  locale = 'en',
  isHome,
  isSalesPage = false,
  crawlable,
  isBrandPage,
  isFullBleed,
  btnClassName,
  statusCode,
  homeVariation,
  isArticleOrBlogPage = false,
  isNonBrandPage = false,
  isPzn,
  trustPilotData = {},
  showABTestHeader = false,
  outageBanner = {},
  megamenuData = null,
  HeaderComponent = ActualHeader,
}) => {
  const [isQuizNudgeFlagOn, setIsQuizNudgeFlagOn] = useState(false)
  const [isQuizOpen, setIsQuizOpen] = useState(false)
  const [isPageViewSent, setIsPageViewSent] = useState(false)
  const [isChatbotExperimentReady, setIsChatbotExperimentReady] =
    useState(false)
  const [showOutageBanner, setShowOutageBanner] = useState(false)
  const [isReturningVisitor, setIsReturningVisitor] = useState(false)
  const [sideBarOpen, setSideBarOpen] = useState(false)
  const [sideBarInit, setSideBarInit] = useState(false)
  const leadId = useLeadId()
  const { track, view } = useAnalytics()
  const serversideExperiments = useServerSideExperiments()
  const {
    setVariations,
    variations: allVariations,
    setFeatureFlags,
    setVariation,
    getFeatureFlags,
  } = useFeatureFlags()
  const pageLoaded = useRef(false)
  const router = useRouter()
  const [insightsLoaded, setInsightsLoaded] = useState(false)
  const chatbotFetched = useRef(false)
  const browserIdRetries = useRef(0)
  const quizFlagRequested = useRef(false)

  // Snapshot showForm before ClientDashboardLoginProvider deletes it
  const cameFromLoginRef = useRef(null)
  if (cameFromLoginRef.current === null) {
    cameFromLoginRef.current =
      typeof window !== 'undefined' && getCookie('showForm') === 'true'
  }

  const {
    shouldShowInactiveLeadWelcomePopup,
    isCandidateForWelcomePopup,
    pznData,
    closeInactiveLeadPopup,
  } = useInactiveLeadPznPopup({
    enabled: isHome || isPzn,
    insightsLoaded,
    routerAsPath: router.asPath,
    sideBarOpen,
  })

  // DCON-3903 — Prospect quiz nudge, non-brand pages only
  const isQuizNudgeActive = isNonBrandPage && isQuizNudgeFlagOn

  const {
    showNudge: showQuizNudge,
    consentBannerOffset,
    handleEngage: handleQuizNudgeEngage,
    handleDismiss: handleQuizNudgeDismiss,
  } = useProspectQuizNudge({
    enabled: isQuizNudgeActive && !cameFromLoginRef.current,
    // track() only attaches experiments once view() has run, so hold the
    // nudge until then or its popup_view goes out without them (DCON-3902).
    isBlocked: shouldShowInactiveLeadWelcomePopup || !isPageViewSent,
  })

  // The open request lives here, so it survives the lazy quiz chunk loading.
  const handleTakeQuiz = useCallback(() => {
    handleQuizNudgeEngage()
    setIsQuizOpen(true)
  }, [handleQuizNudgeEngage])

  const handleQuizClose = useCallback(() => setIsQuizOpen(false), [])

  // Merge experiments after fetchChatbot function runs
  useEffect(() => {
    if (!isChatbotExperimentReady) return

    const variationsArray = Object.keys(allVariations).map((key) => ({
      experimentSection: key,
      variation: allVariations[key],
    }))
    if (variationsArray.length > 0) {
      view(statusCode, {}, null, variationsArray)
      console.log('Syncing Analytics with latest variations:', variationsArray)
      setIsPageViewSent(true)
    }
  }, [allVariations, view, statusCode, isChatbotExperimentReady])

  const handleClick = useCallback(() => {
    setSideBarOpen(!sideBarOpen)
    setSideBarInit(true)
    const data = {
      nav_link_section: 'Header',
      click_type: 'Menu Click',
      click_id: 'Menu',
      click_text: 'FDR-Web | Menu',
      track_event: 'global_header',
    }
    track(
      data,
      {
        event_type: 'track',
      },
      'click',
      statusCode
    )
  }, [sideBarOpen, track, statusCode])

  useEffect(() => {
    if (typeof window === 'undefined') return undefined

    let cancelled = false

    const fetchInsights = async () => {
      try {
        const { getUserInsights } = await import(
          '@achieve/cx-event-horizon/browser'
        )
        if (cancelled) return

        console.log('+++User insights fetched successfully')
        const res = await getUserInsights()
        console.log({ getUserInsights: res })
        const segmentType = res?.insights?.profileInsights?.segmentType
        console.log('segmentType', segmentType)

        const isNewUser = [
          'repeat_anonymous_visitor',
          'first_time_anonymous_visitor',
          undefined,
        ].includes(segmentType)

        // Check if we have a stored value from previous page load
        const storedIsNewUser = sessionStorage.getItem('isNewUser')

        // If getUserInsights returns empty data but we have a stored value, use it
        let finalIsNewUser = isNewUser
        if (!res?.insights?.profileInsights && storedIsNewUser !== null) {
          finalIsNewUser = storedIsNewUser === 'true'
          console.log(
            'getUserInsights returned empty, using stored isNewUser:',
            finalIsNewUser
          )
        } else if (res?.insights?.profileInsights) {
          // Store the value when we have valid profile insights
          sessionStorage.setItem('isNewUser', String(isNewUser))
          console.log('Stored isNewUser in sessionStorage:', isNewUser)
        } else if (storedIsNewUser === null) {
          // First time and getUserInsights returned empty - store default value
          sessionStorage.setItem('isNewUser', String(isNewUser))
          console.log(
            'First load with empty getUserInsights, storing default isNewUser:',
            isNewUser
          )
        }

        const isReturning = !finalIsNewUser
        if (isReturning) {
          const trackDataReturningVisitor = {
            event_name:
              'ID Check Segmentation Events - repeat_anonymous_visitor',
            event_action: 'Track Visitor',
          }
          trackVisitor(
            'ID Check Segmentation Events - repeat_anonymous_visitor',
            trackDataReturningVisitor
          )
          // Set visitor flag for session
          if (sessionStorage.getItem('returningVisitor') !== 'true') {
            sessionStorage.setItem('returningVisitor', 'true')
          }

          setIsReturningVisitor(true)
        } else {
          const trackDataFirstVisitor = {
            event_name:
              'ID Check Segmentation Events - first_time_anonymous_visitor',
            event_action: 'Track Visitor',
          }
          trackVisitor(
            'ID Check Segmentation Events - first_time_anonymous_visitor',
            trackDataFirstVisitor
          )
          setIsReturningVisitor(false)
        }
      } catch (error) {
        console.error('Error fetching user insights:', { error })
        // Try to use stored value on error
        const storedIsNewUser = sessionStorage.getItem('isNewUser')
        if (storedIsNewUser !== null) {
          const isReturning = storedIsNewUser === 'false'
          setIsReturningVisitor(isReturning)
          console.log(
            'Error occurred, using stored isNewUser:',
            storedIsNewUser
          )
        } else {
          setIsReturningVisitor(false)
        }
      } finally {
        if (!cancelled) setInsightsLoaded(true)
      }
    }

    const startedAt = Date.now()
    const insightsDeferMs = 2500

    const run = () => {
      if (cancelled) return
      const elapsed = Date.now() - startedAt
      if (elapsed < insightsDeferMs) {
        window.setTimeout(run, insightsDeferMs - elapsed)

        return
      }

      fetchInsights()
    }

    let idleId
    if ('requestIdleCallback' in window) {
      idleId = window.requestIdleCallback(run, { timeout: 6000 })
    } else {
      idleId = window.setTimeout(run, insightsDeferMs)
    }

    return () => {
      cancelled = true
      if (typeof idleId === 'number') {
        window.clearTimeout(idleId)
      } else if (idleId != null && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleId)
      }
    }
  }, [])

  useEffect(() => {
    const body = document.getElementsByTagName('body')[0]
    sideBarOpen
      ? body.classList.add('h-screen', 'overflow-hidden')
      : body.classList.remove('h-screen', 'overflow-hidden')
  }, [sideBarOpen])

  const setViewExperiments = useCallback(async () => {
    const experiments = []

    // AB_FLAG_ANCHOR:SERVER_DECLARATIONS — tools/add-ab-flag.js inserts server-side declarations below. Keep this line.
    const HOMEPAGE_SIMPLE_CALCULATOR_TEST =
      serversideExperiments[featureToggles.HOMEPAGE_SIMPLE_CALCULATOR_TEST]
    const LARGE_VS_SMALL_HERO_IMAGE =
      serversideExperiments[featureToggles.LARGE_VS_SMALL_HERO_IMAGE]
    const TALK_TO_US = serversideExperiments[featureToggles.TALK_TO_US]
    const shouldEmitTalkToUs =
      TALK_TO_US &&
      TALK_TO_US.variation !== 'off' &&
      isTalkToUsPath(router.asPath)
    try {
      const flags = []

      // AB_FLAG_ANCHOR:CLIENT_FLAGS — tools/add-ab-flag.js inserts client-side flag pushes below. Keep this line.

      // Homepage Simple Calculator Test
      if (isHome) flags.push(featureToggles.HOMEPAGE_SIMPLE_CALCULATOR_TEST)

      // DCON-3903 — Prospect quiz nudge, non-brand pages only
      if (isNonBrandPage) {
        quizFlagRequested.current = true
        flags.push(featureToggles.PROSPECT_QUIZ_NUDGE)
      }

      const ldWebsiteFdrContext = {}

      let featuresFlag = {}
      if (flags.length > 0) {
        const response = await axios.post('/api/ab-testing/', {
          flags,
          leadId: leadId,
          ldWebsiteFdrContext,
        })

        featuresFlag = response.data?.features || {}
      }

      // AB_FLAG_ANCHOR:CLIENT_EXPERIMENTS — tools/add-ab-flag.js inserts client-side experiment pushes below. Keep this line.

      // Homepage Simple Calculator Test
      if (isHome)
        experiments.push({
          experimentSection:
            experimentSectionByVariation[
              featureToggles.HOMEPAGE_SIMPLE_CALCULATOR_TEST
            ],
          variation:
            featuresFlag[featureToggles.HOMEPAGE_SIMPLE_CALCULATOR_TEST] ||
            'control',
        })

      // AB_FLAG_ANCHOR:SERVER_EXPERIMENTS — tools/add-ab-flag.js inserts server-side experiment pushes below. Keep this line.
      if (isHome && LARGE_VS_SMALL_HERO_IMAGE) {
        experiments.push(LARGE_VS_SMALL_HERO_IMAGE)
      }

      if (shouldEmitTalkToUs) {
        experiments.push(TALK_TO_US)
      }

      // DCON-3903 — "Prospect Quiz Nudge"
      const PROSPECT_QUIZ_NUDGE = isNonBrandPage
        ? featuresFlag[featureToggles.PROSPECT_QUIZ_NUDGE]
        : undefined

      if (PROSPECT_QUIZ_NUDGE && PROSPECT_QUIZ_NUDGE !== 'off') {
        experiments.push({
          experimentSection:
            experimentSectionByVariation[featureToggles.PROSPECT_QUIZ_NUDGE],
          variation: PROSPECT_QUIZ_NUDGE,
        })
      }

      setFeatureFlags(featuresFlag)
      setIsQuizNudgeFlagOn(
        !!PROSPECT_QUIZ_NUDGE &&
          PROSPECT_QUIZ_NUDGE !== 'off' &&
          PROSPECT_QUIZ_NUDGE !== 'control'
      )
    } catch (err) {
      // AB_FLAG_ANCHOR:SERVER_EXPERIMENTS_FALLBACK — tools/add-ab-flag.js inserts server-side fallback experiment pushes below. Keep this line.
      // Homepage Simple Calculator Test (fallback)
      if (isHome && HOMEPAGE_SIMPLE_CALCULATOR_TEST) {
        experiments.push(HOMEPAGE_SIMPLE_CALCULATOR_TEST)
      }

      if (isHome && LARGE_VS_SMALL_HERO_IMAGE) {
        experiments.push(LARGE_VS_SMALL_HERO_IMAGE)
      }

      if (shouldEmitTalkToUs) {
        experiments.push(TALK_TO_US)
      }
    }

    pageLoaded.current = true
    setVariations(experiments)
  }, [
    leadId,
    setVariations,
    isHome,
    isArticleOrBlogPage,
    isNonBrandPage,
    serversideExperiments,
    setFeatureFlags,
    router.asPath,
  ])

  const fetchChatbotExperiment = useCallback(async () => {
    if (chatbotFetched.current || !insightsLoaded) return

    const browserId = getCookie('browser_id')
    if (!browserId) {
      // Without browser_id the chatbot flag can't be evaluated. Stop after
      // BROWSER_ID_MAX_RETRIES so the page view (and the quiz nudge, which
      // waits for it) still go out, just without the chatbot experiment.
      if (browserIdRetries.current >= BROWSER_ID_MAX_RETRIES) {
        chatbotFetched.current = true
        setIsChatbotExperimentReady(true)

        return
      }

      browserIdRetries.current += 1
      console.log('Browser Id not ready, retrying in 500ms...')
      setTimeout(fetchChatbotExperiment, BROWSER_ID_RETRY_MS)

      return
    }

    chatbotFetched.current = true

    const chatbotFlag = featureToggles.FDR_CHATBOT
    console.log('b_id', getCookie('browser_id'))
    console.log('v_id', getCookie('eh_visit_id'))
    console.log('l_id', leadId)
    const ldWebsiteFdrContext = {
      [chatbotFlag]: {
        userKey: getCookie('browser_id'),
        projectType: 'website-fdr',
        attributes: {
          customAttributes: {
            userType: isReturningVisitor ? 'returning' : 'new',
            utmSource: router.query?.utm_source || '',
          },
        },
      },
    }

    try {
      const response = await axios.post('/api/ab-testing/', {
        flags: [chatbotFlag],
        ldWebsiteFdrContext,
      })
      const variation = response.data?.features?.[chatbotFlag] || 'Control'
      // 1. Get CURRENT flags and merge manually
      const currentFlags = getFeatureFlags()

      setFeatureFlags({ ...currentFlags, [chatbotFlag]: variation })
      setVariation(experimentSectionByVariation[chatbotFlag], variation)
    } catch (err) {
      console.error('Chatbot experiment fetch failed', err)
    } finally {
      setIsChatbotExperimentReady(true)
    }
  }, [
    getFeatureFlags,
    insightsLoaded,
    isReturningVisitor,
    leadId,
    router.query?.utm_source,
    setFeatureFlags,
    setVariation,
  ])

  useEffect(() => {
    if (pageLoaded.current) return
    setViewExperiments()
  }, [setViewExperiments])

  // DCON-3903 — The Layout survives client-side navigation between catch-all
  // pages, and setViewExperiments only runs once. Request the quiz flag the
  // first time the page becomes non-brand if that first run did not.
  useEffect(() => {
    if (!isNonBrandPage || quizFlagRequested.current) return
    quizFlagRequested.current = true

    const flag = featureToggles.PROSPECT_QUIZ_NUDGE
    axios
      .post('/api/ab-testing/', {
        flags: [flag],
        leadId,
        ldWebsiteFdrContext: {},
      })
      .then((response) => {
        const variation = response.data?.features?.[flag]
        if (!variation || variation === 'off') return

        setFeatureFlags({ ...getFeatureFlags(), [flag]: variation })
        setVariation(experimentSectionByVariation[flag], variation)
        setIsQuizNudgeFlagOn(variation !== 'control')
      })
      .catch(() => {
        // Best-effort: the nudge stays off, as it does on a failed first load.
      })
  }, [isNonBrandPage, leadId, getFeatureFlags, setFeatureFlags, setVariation])

  useEffect(() => {
    return () => {
      const body = document.getElementsByTagName('body')[0]
      body.classList.remove('h-screen', 'overflow-hidden')
    }
  }, [])

  useEffect(() => {
    if (insightsLoaded) {
      fetchChatbotExperiment()
    }
  }, [insightsLoaded, fetchChatbotExperiment])

  useEffect(() => {
    if (
      outageBanner &&
      outageBanner?.isAvailable &&
      sessionStorage.getItem('outage_banner') === 'true'
    ) {
      setShowOutageBanner(true)
    }
  }, [outageBanner, outageBanner?.isAvailable])

  return (
    <>
      <ClientDashboardLoginProvider>
        <div
          className={`${styles.layout} ${
            sideBarInit
              ? sideBarOpen
                ? styles.menuOpen
                : styles.menuClosed
              : ''
          }`}
        >
          <div className={styles.site}>
            {outageBanner && outageBanner.isAvailable && (
              <OutageBanner
                isAvailable={outageBanner.isAvailable}
                message={outageBanner.enMessage}
                onClose={() => setShowOutageBanner(false)}
              />
            )}
            {megamenuData ? (
              <Megamenu megamenuData={megamenuData} />
            ) : (
              <div
                className={`flex justify-start print:hidden lg:justify-center`}
              >
                <HeaderComponent
                  sideBarOpen={sideBarOpen}
                  setSideBarOpen={setSideBarOpen}
                  withHeaderTel={withHeaderTel}
                  locale={locale}
                  btnClassName={btnClassName}
                  statusCode={statusCode}
                  isHome={isHome}
                  isSalesPage={isSalesPage}
                  isArticleOrBlogPage={isArticleOrBlogPage}
                  isBrandPage={isBrandPage}
                  trustPilotData={trustPilotData}
                  showABTestHeader={showABTestHeader}
                  handleSideNavClick={handleClick}
                  flotingPhoneNumber={defaultNumber}
                />
              </div>
            )}
            <main
              className={classNames('mt-18 lg:mt-0', {
                'mt-0!': showOutageBanner || megamenuData,
              })}
            >
              {children}
            </main>
            <Footer
              footerClassName={footerClassName}
              locale={locale}
              crawlable={crawlable}
              isHome={isHome}
              isBrandPage={isBrandPage}
              statusCode={statusCode}
              isFullBleed={isFullBleed}
              homeVariation={homeVariation}
              shouldShowInactiveLeadWelcomePopup={isCandidateForWelcomePopup}
            />
          </div>
          {shouldShowInactiveLeadWelcomePopup && (
            <LazyInactiveLeadPopup
              pznData={pznData}
              onClose={closeInactiveLeadPopup}
              leadId={leadId}
            />
          )}
          {showQuizNudge && (
            <LazyProspectQuizNudge
              onTakeQuiz={handleTakeQuiz}
              onDismiss={handleQuizNudgeDismiss}
              consentBannerOffset={consentBannerOffset}
            />
          )}
          {/* DCON-3904 — preloaded with the nudge flag; kept mounted while open */}
          {(isQuizNudgeActive || isQuizOpen) && (
            <LazyProspectQuiz open={isQuizOpen} onClose={handleQuizClose} />
          )}
          <ClientDashboardLoginOverlayGate
            footerProps={{
              footerClassName,
              locale,
              crawlable,
              isHome,
              isBrandPage,
              statusCode,
              isFullBleed,
              homeVariation,
              shouldShowInactiveLeadWelcomePopup: isCandidateForWelcomePopup,
            }}
          />
        </div>
      </ClientDashboardLoginProvider>
    </>
  )
}

Layout.defaultProps = {
  locale: 'en',
  footerClassName: '',
  withHeaderTel: true,
  crawlable: false,
  isBrandPage: false,
  isHome: false,
  isArticleOrBlogPage: false,
  isNonBrandPage: false,
  isFullBleed: false,
  btnClassName: undefined,
  renderReviewJsonLd: false,
  experiments: [],
  isPzn: false,
  iSalesHeader: false,
  trustPilotData: {},
  forceFullRequest: false,
}

Layout.propTypes = {
  locale: propTypes.string,
  withHeaderTel: propTypes.bool,
  children: renderable.isRequired,
  footerClassName: propTypes.string,
  crawlable: propTypes.bool,
  isBrandPage: propTypes.bool,
  isHome: propTypes.bool,
  isFullBleed: propTypes.bool,
  isArticleOrBlogPage: propTypes.bool,
  isNonBrandPage: propTypes.bool,
  btnClassName: propTypes.string,
  statusCode: propTypes.number,
  renderReviewJsonLd: propTypes.bool,
  experiments: propTypes.array.isRequired,
  isPzn: propTypes.bool,
  iSalesHeader: propTypes.bool,
  forceFullRequest: propTypes.bool,
  HeaderComponent: propTypes.elementType,
}

export default Layout
